import axios from "axios";
import { Edit, Loader2 } from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";
import EditorTextArea from "../../../components/EditorTextArea";
import { Button } from "../../../components/ui/button";
import { Card, CardContent } from "../../../components/ui/card";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "../../../components/ui/drawer";
import { Input } from "../../../components/ui/input";
import { useLanguage } from "../../../context/LanguageContext";
import { BaseUrlApi, ErrorMessage } from "../../../lib/api";
import { LevelData } from "../../../lib/languages";

const DrawerEditLevels = ({ level, course, getAllLevels }) => {
  const { language } = useLanguage();
  const t = LevelData[language] || LevelData["en"];

  const [open, setOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    title_en: level?.title?.en,
    title_ar: level?.title?.ar,
    description_en: level?.description?.en,
    description_ar: level?.description?.ar,
  });

  const handleEditLevel = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData();

      if (form.title_ar || form.title_en) {
        formData.append(
          "title",
          JSON.stringify({ ar: form.title_ar, en: form.title_en })
        );
      }

      if (form.description_ar || form.description_en) {
        formData.append(
          "description",
          JSON.stringify({
            ar: form.description_ar,
            en: form.description_en,
          })
        );
      }

      await axios.post(
        `${BaseUrlApi}/courses/${course.id}/levels/${level.bid}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      toast(t.levelUpdatedToast, {
        description: t.levelUpdatedDescription,
      });

      await getAllLevels();
      setOpen(false);
      // Optionally clear form here if needed.
    } catch (error) {
      setErrors(ErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <Button
          variant="ghost"
          className="flex justify-start items-center gap-2 w-full h-8"
        >
          <Edit className="h-4 w-4" />
          <span>{t.edit}</span>
        </Button>
      </DrawerTrigger>
      <DrawerContent dir={language === "ar" ? "rtl" : "ltr"}>
        <div className="h-[calc(100vh-2rem)] md:h-fit max-w-7xl w-full mx-auto overflow-auto hide-scrollbar space-y-4 p-4">
          {/* Header */}
          <DrawerHeader className="flex items-center justify-between flex-wrap gap-4 p-0 pb-2 w-full">
            <div className="w-full sm:w-fit">
              <DrawerTitle className="font-bold text-2xl">
                {t.editLevel}
              </DrawerTitle>
              <DrawerDescription className="font-medium text-sm">
                {t.editLevelDescription}
              </DrawerDescription>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-fit">
              <Button
                className="w-full sm:w-fit h-10 bg-blue-600 text-white hover:bg-blue-600/80 rounded-xl z-10"
                onClick={handleEditLevel}
                disabled={loading}
              >
                {loading && (
                  <div className="animate-spin">
                    <Loader2 />
                  </div>
                )}
                {t.editLevel}
              </Button>
              <DrawerClose asChild>
                <Button
                  variant="outline"
                  className="w-full sm:w-fit h-10 border-2 border-blue-600 text-blue-600 hover:text-blue-500 rounded-xl"
                >
                  {t.cancel}
                </Button>
              </DrawerClose>
            </div>
          </DrawerHeader>
          {/* General information */}
          <Card>
            <CardContent className="p-4 space-y-4 flex-1 flex flex-col w-full">
              <p className="font-bold text-xl border-b pb-2">
                General information
              </p>
              <div className="space-y-4 flex-1 flex flex-col">
                <div className="flex items-center gap-4">
                  <div className="flex flex-col gap-2 w-full">
                    <label htmlFor="title_en" className="text-sm">
                      {t.titleEn}
                    </label>
                    <Input
                      id="title_en"
                      name="title_en"
                      type="text"
                      placeholder={t.enterTitleEn}
                      onChange={(e) =>
                        setForm({ ...form, title_en: e.target.value })
                      }
                      value={form.title_en}
                    />
                    {errors?.title && (
                      <p className="text-red-500 font-semibold text-sm">
                        {errors.title[0]}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 w-full">
                    <label htmlFor="title_ar" className="text-sm">
                      {t.titleAr}
                    </label>
                    <Input
                      id="title_ar"
                      name="title_ar"
                      type="text"
                      placeholder={t.enterTitleAr}
                      onChange={(e) =>
                        setForm({ ...form, title_ar: e.target.value })
                      }
                      value={form.title_ar}
                    />
                    {errors?.title && (
                      <p className="text-red-500 font-semibold text-sm">
                        {errors.title[0]}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-2 flex-1">
                  <label htmlFor="description" className="text-sm">
                    {t.description}
                  </label>
                  <EditorTextArea
                    form={form}
                    en="description_en"
                    ar="description_ar"
                    setForm={setForm}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default DrawerEditLevels;
